import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import BlogCard from '../../components/common/BlogCard';
import { blogService } from '../../services/blogService';
import classes from './styles/categoryBlogs.module.css';

// ── Structured data ──────────────────────────────────────────
const buildCategorySchema = (decodedCategory, encodedCategory, total) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": `${decodedCategory} Articles | FetchJobs Blog`,
  "description": `Browse all ${total} articles in the ${decodedCategory} category on FetchJobs Blog.`,
  "url": `https://www.fetchjobs.co/blog/category/${encodedCategory}`,
  "isPartOf": { "@type": "WebSite", "url": "https://www.fetchjobs.co" },
  "about": {
    "@type": "Thing",
    "name": decodedCategory
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",                   "item": "https://www.fetchjobs.co" },
      { "@type": "ListItem", "position": 2, "name": "Blog",                   "item": "https://www.fetchjobs.co/blog" },
      { "@type": "ListItem", "position": 3, "name": decodedCategory,          "item": `https://www.fetchjobs.co/blog/category/${encodedCategory}` }
    ]
  }
});

// ── Shared Helmet ────────────────────────────────────────────
const CategoryHelmet = ({ decodedCategory, encodedCategory, total, isLoading }) => {
  const canonicalUrl = `https://www.fetchjobs.co/blog/category/${encodedCategory}`;
  const metaTitle    = `${decodedCategory} Articles | FetchJobs Blog`;
  const metaDesc     = `Browse the latest articles on ${decodedCategory} — hiring tips, career advice, and remote work insights on FetchJobs Blog.`;

  return (
    <Helmet>
      {/* Core */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDesc} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type"        content="website" />
      <meta property="og:title"       content={metaTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:site_name"   content="FetchJobs" />
      <meta property="og:image"       content="https://www.fetchjobs.co/logo.png" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary" />
      <meta name="twitter:title"       content={metaTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image"       content="https://www.fetchjobs.co/logo.png" />

      {/* Robots — noindex while loading, index once content is ready */}
      <meta name="robots" content={isLoading ? "noindex" : "index, follow"} />

      {/* CollectionPage schema */}
      {!isLoading && (
        <script type="application/ld+json">
          {JSON.stringify(buildCategorySchema(decodedCategory, encodedCategory, total))}
        </script>
      )}
    </Helmet>
  );
};

// ── Component ────────────────────────────────────────────────
const CategoryBlogs = () => {
  const { category } = useParams();
  const [blogs, setBlogs]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 6, total: 0, pages: 1 });
  const [hasMore, setHasMore]       = useState(true);
  const lastBlogRef = useRef();

  const decodedCategory = decodeURIComponent(category);
  const encodedCategory = encodeURIComponent(decodedCategory);

  const fetchBlogs = useCallback(async (page, reset = false) => {
    reset ? setLoading(true) : setLoadingMore(true);

    const result = await blogService.getBlogsByCategory(decodedCategory, page, 6);

    if (result.success) {
      setBlogs(prev => reset ? result.blogs : [...prev, ...result.blogs]);
      setPagination(result.pagination);
      setHasMore(page < result.pagination.pages);
    }

    reset ? setLoading(false) : setLoadingMore(false);
  }, [category]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchBlogs(1, true); }, [category, fetchBlogs]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) fetchBlogs(pagination.page + 1, false);
  }, [loadingMore, hasMore, loading, pagination.page, fetchBlogs]);

  useEffect(() => {
    if (loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && hasMore && !loadingMore) loadMore(); },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    );
    const currentRef = lastBlogRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); observer.disconnect(); };
  }, [loading, loadingMore, hasMore, loadMore, blogs.length]);

  if (loading && blogs.length === 0) {
    return (
      <div className={classes.container}>
        <CategoryHelmet
          decodedCategory={decodedCategory}
          encodedCategory={encodedCategory}
          total={0}
          isLoading={true}
        />
        <div className={classes.loadingContainer}>
          <div className={classes.loader}></div>
          <p>Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <CategoryHelmet
        decodedCategory={decodedCategory}
        encodedCategory={encodedCategory}
        total={pagination.total}
        isLoading={false}
      />

      <div className={classes.header}>
        <nav className={classes.breadcrumb} aria-label="Breadcrumb">
          <Link to="/blog">Blog</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{decodedCategory}</span>
        </nav>
        <h1 className={classes.title}>{decodedCategory}</h1>
        <p className={classes.subtitle}>
          {pagination.total} {pagination.total === 1 ? 'article' : 'articles'} in this category
        </p>
      </div>

      {blogs.length === 0 ? (
        <div className={classes.emptyContainer}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#004AA6" strokeWidth="2"/>
            <path d="M2 8H22" stroke="#004AA6" strokeWidth="2"/>
          </svg>
          <h3>No articles found</h3>
          <p>There are no published articles in this category yet.</p>
          <Link to="/blog" className={classes.backBtn}>Browse All Articles</Link>
        </div>
      ) : (
        <>
          <div className={classes.blogsGrid}>
            {blogs.map((blog, index) => (
              <div
                key={blog._id}
                ref={index === blogs.length - 1 ? lastBlogRef : null}
              >
                <BlogCard blog={blog} />
              </div>
            ))}
          </div>

          {loadingMore && (
            <div className={classes.loadingMore}>
              <div className={classes.loaderSmall}></div>
              <p>Loading more articles...</p>
            </div>
          )}

          {!hasMore && blogs.length > 0 && (
            <div className={classes.endMessage}>
              <p>You've reached the end of this category!</p>
              <Link to="/blog" className={classes.browseLink} aria-label="Browse all blog articles">
                Browse All Articles →
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryBlogs;